/**
 * Dashboard Widget Container
 *
 * Reusable wrapper component for dashboard widgets
 */

import { type ReactNode } from 'react';
import { WidgetSkeleton } from '../shared/Skeleton';
import { formatTime } from '../../lib/dateFormat';
import type { WidgetConfig, WidgetState } from './types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import './DashboardWidget.css';

export interface DashboardWidgetProps<T = unknown> {
  /** Widget configuration */
  config: WidgetConfig;
  /** Widget state */
  state: WidgetState<T>;
  /** Widget content */
  children: ReactNode;
  /** Refresh callback */
  onRefresh: () => void;
  /** Expand callback */
  onExpand?: () => void;
  /** Extra header action rendered before refresh/expand (e.g. a "Configure widget" gear, issue #489). */
  headerAction?: ReactNode;
}

export function DashboardWidget<T = unknown>({
  config,
  state,
  children,
  onRefresh,
  onExpand,
  headerAction,
}: DashboardWidgetProps<T>): JSX.Element {
  const CardElement = 'forge-card' as React.ElementType;
  const IconButtonElement = 'forge-icon-button' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  return (
    <CardElement className={`dashboard-widget dashboard-widget--${config.size}`}>
      {/* Widget Header */}
      <div className="dashboard-widget__header">
        <h3 className="dashboard-widget__title forge-typography--label1">{config.title}</h3>
        <div className="dashboard-widget__actions">
          {headerAction}
          {config.showRefresh && (
            <IconButtonElement
              density="dense"
              onClick={onRefresh}
              disabled={state.loading}
              aria-label="Refresh widget"
            >
              <IconElement name="refresh" />
            </IconButtonElement>
          )}
          {config.showExpand && onExpand && (
            <IconButtonElement density="dense" onClick={onExpand} aria-label="Expand widget">
              <IconElement name="open_in_full" />
            </IconButtonElement>
          )}
        </div>
      </div>

      {/* Widget Content */}
      <div className="dashboard-widget__content">
        {state.loading && !state.data ? (
          <div className="dashboard-widget__loading">
            <WidgetSkeleton />
          </div>
        ) : state.error ? (
          <div className="dashboard-widget__error">
            <IconElement name="error" className="dashboard-widget__error-icon" />
            <p className="forge-typography--body2">{state.error}</p>
            <ForgeButton type="button" variant="text" onClick={onRefresh}>
              Try Again
            </ForgeButton>
          </div>
        ) : (
          children
        )}
      </div>

      {/* Widget Footer */}
      {state.lastRefresh && (
        <div className="dashboard-widget__footer">
          <span className="forge-typography--caption">
            Last updated: {formatTime(state.lastRefresh, { timeStyle: 'medium' })}
          </span>
        </div>
      )}
    </CardElement>
  );
}
